import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';

const TeamCreation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    city: '',
    state: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeamForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateLeagueId = () => {
    // Generate a simple league ID
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const createTeam = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!teamForm.name.trim()) {
        setError('Team name is required');
        setLoading(false);
        return;
      }

      if (!teamForm.city.trim() || !teamForm.state.trim()) {
        setError('City and state are required');
        setLoading(false);
        return;
      }

      if (!teamForm.contactName.trim() || !teamForm.contactEmail.trim()) {
        setError('Contact name and email are required');
        setLoading(false);
        return;
      }

      // Check if team name already exists
      const teamQuery = query(
        collection(db, 'teams'),
        where('name', '==', teamForm.name.trim())
      );
      const existingTeams = await getDocs(teamQuery);
      
      if (!existingTeams.empty) {
        setError('A team with this name already exists');
        setLoading(false);
        return;
      }

      // Generate team ID and league ID
      const teamId = doc(collection(db, 'teams')).id;
      const leagueId = generateLeagueId();

      // Create team document
      const newTeam = {
        id: teamId,
        name: teamForm.name.trim(),
        description: teamForm.description.trim(),
        location: {
          city: teamForm.city.trim(),
          state: teamForm.state.trim()
        },
        contactPerson: {
          name: teamForm.contactName.trim(),
          email: teamForm.contactEmail.trim(),
          phone: teamForm.contactPhone.trim()
        },
        leagueId: leagueId,
        createdBy: user.uid,
        createdAt: new Date(),
        analytics: {
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          totalScore: 0,
          averageScore: 0,
          bestScore: 0,
          winPercentage: 0,
          lastGameDate: null,
          currentStreak: 0,
          longestStreak: 0
        },
        rankings: {
          city: { position: 0, totalTeams: 0, percentile: 0 },
          state: { position: 0, totalTeams: 0, percentile: 0 },
          national: { position: 0, totalTeams: 0, percentile: 0 }
        }
      };

      // Save team to Firestore
      await setDoc(doc(db, 'teams', teamId), newTeam);

      // Update user document with team reference
      await setDoc(doc(db, 'users', user.uid), {
        teamId: teamId,
        role: 'league_user'
      }, { merge: true });

      // Redirect to team dashboard
      navigate('/team/dashboard');
      
    } catch (error) {
      console.error('Error creating team:', error);
      setError('Failed to create team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Logo />
              <h1 className="ml-4 text-xl font-semibold text-white">Create Team</h1>
            </div>
            <button
              onClick={() => navigate('/team/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-6">Create Your Team</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded-lg">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={createTeam} className="space-y-6">
            {/* Team Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Team Name *
              </label>
              <input
                type="text"
                name="name"
                value={teamForm.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter your team name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Team Description
              </label>
              <textarea
                name="description"
                value={teamForm.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Tell us about your team..."
              />
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={teamForm.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter city"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  name="state"
                  value={teamForm.state}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter state"
                  required
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Contact Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={teamForm.contactName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter contact person's name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={teamForm.contactEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter contact email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={teamForm.contactPhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter contact phone (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/team/dashboard')}
                className="px-6 py-2 text-gray-300 hover:text-white"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeamCreation;


